<script setup lang="ts">
import CheckoutRadio from "@/components/checkout/CheckoutRadio.vue"
import { inject, reactive, watch, ref, computed, onMounted } from "vue"
import { useDesignerStore } from "@/stores/designerStore"
import { usePricingStore } from "@/stores/pricing"
import { calculateLabelPrice } from "@/api/frontend"

// ---------------------------------------------------------
//  Injects z parenta (DesignerView.vue)
// ---------------------------------------------------------
const wizard = inject('wizard')
const item = inject<any>('item')
const saveMaterialAndQuantity = inject<any>('saveMaterialAndQuantity')

// ---------------------------------------------------------
//  Stores
// ---------------------------------------------------------
const designerStore = useDesignerStore()
const pricingStore = usePricingStore()

// ---------------------------------------------------------
//  Konštanty
// ---------------------------------------------------------
const qtyOptions = [1, 2, 5, 50, 100, 500]

// lokálny cenník pre rádio možnosti
const priceMap = reactive<Record<number, { total: number, perPiece: string }>>({})

// Custom množstvo
const customQuantity = ref<number | null>(20)
const isCustom = computed(() => !qtyOptions.includes(item.quantity))

// ---------------------------------------------------------
//  Memo (jednoduchá cache nad výpočtom ceny)
// ---------------------------------------------------------
const memo = reactive(new Map<string, { total: number; perPiece: string }>())

function useMemo(key: string, compute: () => { total: number; perPiece: string }) {
  const hit = memo.get(key)
  if (hit) return hit
  const res = compute()
  memo.set(key, res)
  return res
}

// ---------------------------------------------------------
//  Spoľahlivé čítanie rozmerov z designerConfig
//  (vieme, že designerConfig má dieCutMask.dimensions.width/height)
// ---------------------------------------------------------
function getLabelSizeMm(): { w: number; h: number } {
  const cfg = designerStore.designerConfig
  const w = Number(cfg?.dieCutMask?.dimensions?.width ?? 0)
  const h = Number(cfg?.dieCutMask?.dimensions?.height ?? 0)
  return { w, h }
}

// Koľko nálepiek na SRA3 (skúsi obe orientácie a vyberie lepšiu)
function stickersPerSheet(labelW: number, labelH: number): number {
  if (labelW <= 0 || labelH <= 0) return 0
  const [sheetW, sheetH] = pricingStore.sra3_size_mm

  // Orientácia 1: label ako je
  const cols1 = Math.floor(sheetW / labelW)
  const rows1 = Math.floor(sheetH / labelH)
  const sps1 = cols1 * rows1

  // Orientácia 2: label otočený o 90°
  const cols2 = Math.floor(sheetW / labelH)
  const rows2 = Math.floor(sheetH / labelW)
  const sps2 = cols2 * rows2

  // Vyber lepšiu orientáciu
  const sps = Math.max(sps1, sps2)
  return sps > 0 ? sps : 0
}

// Lokálny výpočet ceny pre ľubovoľné qty (z pricing-configu)
function calcLocalPriceForQty(qty: number): { total: number; perPiece: string } {
  const { w, h } = getLabelSizeMm()
  const pricePerSheet = pricingStore.material_prices?.[item.material] ?? 0
  const minSheets = pricingStore.min_archs ?? 1

  // kým nemáme všetko potrebné, zobraz '...'
  if (w <= 0 || h <= 0 || !pricePerSheet) {
    return { total: NaN as unknown as number, perPiece: '...' }
  }

  const sps = stickersPerSheet(w, h)
  if (sps <= 0) {
    return { total: NaN as unknown as number, perPiece: '...' }
  }

  const key = `${item.material}|${w}x${h}|${qty}|${pricePerSheet}|${minSheets}`

  return useMemo(key, () => {
    const neededSheets = Math.max(minSheets, Math.ceil(qty / sps))
    const total = Number((neededSheets * pricePerSheet).toFixed(2))
    const perPiece = (total / qty).toFixed(2)
    return { total, perPiece }
  })
}

// Prepočíta priceMap pre fixné rádia
function rebuildPriceMap() {
  console.log('[MaterialAndQuantity] rebuildPriceMap called')
  console.log('[MaterialAndQuantity] item.material:', item.material)
  console.log('[MaterialAndQuantity] material_prices:', JSON.stringify(pricingStore.material_prices))

  for (const q of qtyOptions) {
    priceMap[q] = calcLocalPriceForQty(q)
    console.log(`[MaterialAndQuantity] priceMap[${q}]:`, priceMap[q])
  }
}

// Total pre aktuálnu voľbu (rádio alebo custom), na zobrazenie
const localTotalForSelected = computed(() => {
  const q = isCustom.value ? (customQuantity.value ?? item.quantity) : item.quantity
  const res = calcLocalPriceForQty(q)
  return Number.isFinite(res.total) ? res.total : 0
})

// ---------------------------------------------------------
//  Lifecycle & watch
// ---------------------------------------------------------
const readyForPricing = ref(false)

onMounted(async () => {
  // 1) načítaj pricing config, ak ešte nie je
  if (!pricingStore.loaded) {
    await pricingStore.fetch()
  }
  console.log('[MaterialAndQuantity] pricingStore loaded:', {
    material_prices: pricingStore.material_prices,
    sra3_size_mm: pricingStore.sra3_size_mm,
    min_archs: pricingStore.min_archs
  })

  readyForPricing.value = true
  // 2) prvý rebuild – ale iba ak máme reálne rozmery
  const { w, h } = getLabelSizeMm()
  console.log('[MaterialAndQuantity] label size:', { w, h })
  console.log('[MaterialAndQuantity] designerConfig:', designerStore.designerConfig)

  if (w > 0 && h > 0) {
    rebuildPriceMap()
  } else {
    console.warn('[MaterialAndQuantity] Invalid label size, prices not calculated')
  }
})

// zmena materiálu → prepočítať
watch(
  () => item.material,
  () => {
    if (!readyForPricing.value) return
    const { w, h } = getLabelSizeMm()
    if (w > 0 && h > 0) rebuildPriceMap()
  }
)

// zmena rozmerov → prepočítať
watch(
  () => {
    const { w, h } = getLabelSizeMm()
    return `${w}x${h}`
  },
  (val) => {
    console.log('[MaterialAndQuantity] dimensions watch triggered:', val, 'readyForPricing:', readyForPricing.value)
    if (!readyForPricing.value) return
    const [w, h] = val.split('x').map(Number)
    if (w > 0 && h > 0) rebuildPriceMap()
  },
  { immediate: true }
)

// ---------------------------------------------------------
//  Custom množstvo
// ---------------------------------------------------------
function setCustom() {
  if (!customQuantity.value || customQuantity.value < 1) {
    customQuantity.value = item.quantity
  }
  item.quantity = customQuantity.value
}

// ---------------------------------------------------------
//  Confirm → autoritatívny prepočet zo servera (zapíše item.price)
// ---------------------------------------------------------
async function confirmQuantity() {
  if (isCustom.value) {
    if (!customQuantity.value || customQuantity.value < 1) return
    item.quantity = customQuantity.value
  }

  // 1️⃣ Prepočet ceny
  const backend = await calculateLabelPrice(
    {
      ...designerStore.designerConfig,
      width_mm: designerStore.canvasSizeInMm.width,
      height_mm: designerStore.canvasSizeInMm.height
    },
    item.material,
    item.quantity
  )
  item.price = backend.total_price

  // 2️⃣ Uloženie položky do košíka
  const result = await saveMaterialAndQuantity?.()

  // 3️⃣ Ak úspešné, presmeruj na /cart (s prefixom jazyka)
  if (result) {
    // zachytí prefix jazyka (napr. /en alebo /de)
    const currentLangPrefix = window.location.pathname.split("/")[1]
    const supportedLangs = ["en", "de", "sk", "cz"]

    const prefix = supportedLangs.includes(currentLangPrefix)
      ? `/${currentLangPrefix}`
      : ""

    window.location.href = `${prefix}/cart`
  } else {
    alert("Failed to save item to cart.")
  }
}
</script>

<template>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
    <!-- MATERIAL -->
    <div class="card-with-dashed-border">
      <div class="card-with-dashed-border-content">
        <div class="text-center">
          <h5 class="text-h5 font-bold text-black tracking-h5 mb-2">Material</h5>
        </div>

        <CheckoutRadio v-model="item.material" name="material" value="paper">
          <template #left>Paper</template>
          <template #right>+ 0€</template>
        </CheckoutRadio>

        <CheckoutRadio v-model="item.material" name="material" value="plastic">
          <template #left>Plastic</template>
          <template #right>+ 1€</template>
        </CheckoutRadio>
      </div>
    </div>

    <!-- QUANTITY -->
    <div class="card-with-dashed-border">
      <div class="card-with-dashed-border-content">
        <h5 class="text-h5 font-bold text-black tracking-h5 text-center mb-2">Quantity</h5>
        <p class="text-regular text-standard mb-3 text-center">Number of stickers</p>

        <div class="space-y-3">
          <!-- Predefined radios -->
          <CheckoutRadio
            v-for="q in qtyOptions"
            :key="q"
            v-model="item.quantity"
            :value="q"
            name="quantity"
          >
            <template #left>
              {{ q }} {{ q === 1 ? 'pc' : 'pcs' }}
            </template>
            <template #right>
              <span v-if="priceMap[q] && Number.isFinite(priceMap[q].total)">
                {{ priceMap[q].total.toFixed(2) }} €
                <br />
                <span class="text-xs opacity-70">
                  {{ priceMap[q].perPiece }} €/piece
                </span>
              </span>
              <span v-else>…</span>
            </template>
          </CheckoutRadio>

          <!-- Custom -->
          <div class="flex items-center py-3 mt-2">
            <input
              type="radio"
              name="quantity"
              class="mr-3"
              :checked="isCustom"
              @change="setCustom"
            />

            <div class="flex-1 font-medium text-black text-[18px]">
              Custom number of stickers
            </div>

            <div class="flex items-center gap-2">
              <input
                type="number"
                min="1"
                class="input-qty"
                v-model.number="customQuantity"
                @focus="setCustom"
              />
              <span class="text-sm">pcs</span>
            </div>
          </div>
        </div>

        <!-- hr -->
        <hr class="my-4 border-gray-300" />

        <!-- Total -->
        <div class="flex justify-between items-center mb-3">
          <span class="font-semibold text-black">Total</span>
          <span class="font-semibold text-black">
            {{ Number.isFinite(localTotalForSelected) ? localTotalForSelected.toFixed(2) : '…' }} €
          </span>
        </div>

        <!-- Confirm -->
        <button class="btn-base btn-black w-full mt-1" @click="confirmQuantity">
          Confirm
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.input-qty {
  padding: 9px 5px;
  text-align: center;
  width: 56px;

  background: #FFFFFF;
  border: 0.5px solid #A3A3A3;
  border-radius: 4px;

  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  color: #000;

  &:focus {
    outline: none;
    border-color: #000;
    box-shadow: 0 0 0 1px #000;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

}
</style>
